import { useState } from "react";
import { toast } from "sonner";
import { LogOut, MessageCircle, ShieldCheck, Smartphone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_COUNTRY, PhoneInput, buildE164, isValidPhone } from "@/components/auth/PhoneInput";

/** Mandatory WhatsApp capture for accounts created via email or Google. */
export default function PhoneGate() {
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY.iso);
  const [phoneDigits, setPhoneDigits] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [smsCodeLength, setSmsCodeLength] = useState(4);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("phone");
  const { refresh, logout, user } = useAuth();

  const phone = buildE164(phoneCountry, phoneDigits);

  const send = async () => {
    setBusy(true);
    try {
      const path = channel === "sms" ? "/auth/phone/sms/request" : "/auth/phone/request";
      const { data } = await api.post(path, { phone });
      setDevCode(data.dev_code || null);
      if (channel === "sms") setSmsCodeLength(data.code_length);
      setSent(true);
      if (channel === "sms") {
        toast.success("Code sent by SMS");
      } else {
        toast[data.delivered ? "success" : "message"](
          data.delivered ? "Code sent on WhatsApp" : "WhatsApp unavailable — code shown below",
        );
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const path = channel === "sms" ? "/auth/phone/sms/verify" : "/auth/phone/verify";
      await api.post(path, { phone, code });
      await refresh();
      toast.success("Mobile number verified");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const requiredCodeLength = channel === "sms" ? smsCodeLength : 6;
  const canSend = isValidPhone(phoneCountry, phoneDigits);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6">
      <div data-testid="phone-gate" className="w-full max-w-sm border border-slate-200 bg-white p-8">
        <div className="flex items-center gap-2 text-[#1A365D]">
          <ShieldCheck className="h-5 w-5" />
          <span className="font-display text-lg font-extrabold tracking-tighter">One last step</span>
        </div>
        <p className="mt-3 text-sm text-slate-600">
          A verified mobile number is required for every NeetOne account — it is how we reach you about
          counselling rounds and your saved shortlists.
        </p>
        <p className="mt-1 text-xs text-slate-400">
          Signed in as {user?.email || user?.name || "your account"}
        </p>

        {!sent ? (
          <div className="mt-7 space-y-4">
            <div className="flex border border-slate-300">
              <button
                data-testid="gate-whatsapp-mode-btn"
                onClick={() => setChannel("phone")}
                className={`flex h-10 flex-1 items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${
                  channel === "phone" ? "bg-[#1A365D] text-white" : "bg-white text-slate-600"
                }`}
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </button>
              <button
                data-testid="gate-sms-mode-btn"
                onClick={() => setChannel("sms")}
                className={`flex h-10 flex-1 items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${
                  channel === "sms" ? "bg-[#1A365D] text-white" : "bg-white text-slate-600"
                }`}
              >
                <Smartphone className="h-3.5 w-3.5" /> SMS
              </button>
            </div>
            <PhoneInput
              label={channel === "sms" ? "SMS number" : "WhatsApp number"}
              testIdPrefix="gate-phone"
              iso={phoneCountry}
              onCountry={setPhoneCountry}
              digits={phoneDigits}
              onDigits={setPhoneDigits}
            />
            <button
              data-testid="gate-send-btn"
              disabled={busy || !canSend}
              onClick={send}
              className="flex h-11 w-full items-center justify-center gap-2 bg-[#1A365D] text-sm font-semibold text-white hover:bg-[#122845] disabled:opacity-40"
            >
              {channel === "sms" ? <Smartphone className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
              {busy ? "Sending…" : channel === "sms" ? "Send SMS code" : "Send WhatsApp code"}
            </button>
          </div>
        ) : (
          <div className="mt-7 space-y-4">
            <p data-testid="gate-channel-status" className="text-sm text-slate-500">
              Enter the code sent by {channel === "sms" ? "SMS" : "WhatsApp"}.
            </p>
            {devCode && (
              <div
                data-testid="gate-dev-notice"
                className="border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              >
                WhatsApp delivery unavailable. Dev code:{" "}
                <strong className="font-mono-num">{devCode}</strong>
              </div>
            )}
            <input
              data-testid="gate-otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, requiredCodeLength))}
              placeholder={"_".repeat(requiredCodeLength)}
              className="h-14 w-full border border-slate-300 px-3 text-center font-mono-num text-2xl tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-[#1A365D]"
            />
            <button
              data-testid="gate-verify-btn"
              disabled={busy || code.length !== requiredCodeLength}
              onClick={verify}
              className="h-11 w-full bg-[#1A365D] text-sm font-semibold text-white hover:bg-[#122845] disabled:opacity-40"
            >
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
            <button
              data-testid="gate-change-btn"
              onClick={() => {
                setSent(false);
                setCode("");
                setDevCode(null);
              }}
              className="w-full text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#1A365D]"
            >
              Change number or method
            </button>
          </div>
        )}

        <button
          data-testid="gate-logout-btn"
          onClick={logout}
          className="mt-8 flex w-full items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-rose-600"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}
